<script setup>
import { onMounted, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';

import ContactsDetailsLayout from 'dashboard/components-next/Contacts/ContactsDetailsLayout.vue';
import CompanyDetails from 'dashboard/components-next/Companies/CompanyDetails.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import CompanyTimeline from 'dashboard/components-next/Companies/CompanySidebar/CompanyTimeline.vue';
import CompanyDeals from 'dashboard/components-next/Companies/CompanySidebar/CompanyDeals.vue';
import CompanyContacts from 'dashboard/components-next/Companies/CompanySidebar/CompanyContacts.vue';
import CompanyNotes from 'dashboard/components-next/Companies/CompanySidebar/CompanyNotes.vue';
import CreateDealDialog from 'dashboard/components-next/Deals/CreateDealDialog.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';
import { useAlert } from 'dashboard/composables';

const store = useStore();
const route = useRoute();
const router = useRouter();

const company = useMapGetter('companies/getCompanyById');
const uiFlags = useMapGetter('companies/getUIFlags');

const activeTab = ref('timeline');
const createDealDialogRef = ref(null);
const createContactDialogRef = ref(null);
const companyDealsRef = ref(null);
const companyContactsRef = ref(null);

const isFetchingItem = computed(() => uiFlags.value.fetchingItem);

const selectedCompany = computed(() => company.value(route.params.companyId));

const showSpinner = computed(() => isFetchingItem.value);

const { t } = useI18n();

const COMPANY_TABS_OPTIONS = [
  { key: 'TIMELINE', value: 'timeline' },
  { key: 'DEALS', value: 'deals' },
  { key: 'CONTACTS', value: 'contacts' },
  { key: 'NOTES', value: 'notes' },
];

const tabs = computed(() => {
  return COMPANY_TABS_OPTIONS.map(tab => ({
    label: t(`COMPANY_DETAILS.SIDEBAR.TABS.${tab.key}`),
    value: tab.value,
  }));
});

const activeTabIndex = computed(() => {
  return COMPANY_TABS_OPTIONS.findIndex(v => v.value === activeTab.value);
});

const goToCompaniesList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/companies?page=1`);
  }
};

const fetchActiveCompany = async () => {
  if (route.params.companyId) {
    await store.dispatch('companies/show', route.params.companyId);
  }
};

const handleTabChange = tab => {
  activeTab.value = tab.value;
};

const handleCompanyUpdated = () => {
  fetchActiveCompany();
};

const handleCreateDeal = () => {
  createDealDialogRef.value?.open();
};

const handleDealCreated = () => {
  companyDealsRef.value?.fetchDeals();
};

const handleAddContact = () => {
  createContactDialogRef.value?.dialogRef?.open();
};

const handleContactCreated = async contact => {
  try {
    const contactWithCompany = {
      ...contact,
      company_id: Number(route.params.companyId),
    };
    await store.dispatch('contacts/create', contactWithCompany);
    createContactDialogRef.value?.onSuccess();
    useAlert(t('COMPANY_DETAILS.SIDEBAR.CONTACTS.CREATE_SUCCESS'));
    companyContactsRef.value?.fetchContacts();
  } catch (error) {
    useAlert(
      error.message || t('COMPANY_DETAILS.SIDEBAR.CONTACTS.CREATE_ERROR')
    );
  }
};

onMounted(() => {
  fetchActiveCompany();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <ContactsDetailsLayout
      :selected-contact="selectedCompany"
      :show-pagination-footer="false"
      wide
      content-max-width-class="max-w-[54rem]"
      sidebar-max-width-class="max-w-lg"
      @go-to-contacts-list="goToCompaniesList"
    >
      <div
        v-if="showSpinner"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>
      <CompanyDetails
        v-else-if="selectedCompany"
        :selected-company="selectedCompany"
        @updated="handleCompanyUpdated"
        @go-to-companies-list="goToCompaniesList"
        @switch-tab="tab => (activeTab = tab)"
      />
      <template #sidebar>
        <div class="px-6">
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            class="w-full [&>button]:w-full bg-n-alpha-black2"
            @tab-changed="handleTabChange"
          />
        </div>
        <div
          v-if="isFetchingItem"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <template v-else>
          <CompanyTimeline
            v-if="activeTab === 'timeline'"
            :company-id="route.params.companyId"
          />
          <CompanyDeals
            v-if="activeTab === 'deals'"
            ref="companyDealsRef"
            :company-id="route.params.companyId"
            @create-deal="handleCreateDeal"
          />
          <CompanyContacts
            v-if="activeTab === 'contacts'"
            ref="companyContactsRef"
            :company-id="route.params.companyId"
            @add-contact="handleAddContact"
          />
          <CompanyNotes
            v-if="activeTab === 'notes'"
            :company-id="route.params.companyId"
          />
        </template>
      </template>
    </ContactsDetailsLayout>

    <!-- Create Deal Dialog -->
    <CreateDealDialog
      ref="createDealDialogRef"
      :initial-company-id="Number(route.params.companyId)"
      @created="handleDealCreated"
    />

    <!-- Create Contact Dialog -->
    <CreateNewContactDialog
      ref="createContactDialogRef"
      @create="handleContactCreated"
    />
  </div>
</template>
